import {
  clearCompletedMemberApplicationOperation,
  getOrCreateMemberApplicationOperation,
  MemberApplicationOperationStorage,
} from "../Members/memberApplicationOperation";
import {
  RecordCmpInvoiceManualPaymentInput,
  RecordCmpInvoiceManualRefundInput,
  RecordCmpMemberManualPaymentInput,
} from "../../../graphql/types";

export type InvoiceFinancialOperationAction =
  | "MANUAL_PAYMENT"
  | "MANUAL_REFUND";

type FinancialOperationAuthorityField = "operationId";

export type RunInvoiceFinancialOperationInput<
  MutationInput extends Record<string, unknown>,
  MutationResult,
> = {
  action: InvoiceFinancialOperationAction;
  chamberId: string;
  createOperationId?: () => string;
  execute: (input: MutationInput) => Promise<MutationResult>;
  fingerprintRequest?: (request: unknown) => Promise<string>;
  input: Omit<MutationInput, FinancialOperationAuthorityField> &
    Partial<Pick<Record<FinancialOperationAuthorityField, string>, FinancialOperationAuthorityField>>;
  invoiceId: string;
  isAuthoritativelyCompleted: (result: MutationResult) => boolean;
  storage?: MemberApplicationOperationStorage;
};

export type PreparedInvoiceFinancialOperationInput<
  MutationInput extends Record<string, unknown>,
> = {
  authorityField: FinancialOperationAuthorityField;
  input: MutationInput;
  operationId: string;
  storageKey: string;
};

export const getInvoiceFinancialOperationStorageKey = ({
  action,
  chamberId,
  invoiceId,
}: {
  action: InvoiceFinancialOperationAction;
  chamberId: string;
  invoiceId: string;
}) =>
  `cmp-invoice-financial-operation:v1:${action}:${encodeURIComponent(
    chamberId
  )}:${encodeURIComponent(invoiceId)}`;

/**
 * Persists only a request fingerprint and opaque UUID in session storage. The
 * exact same financial request therefore survives a lost response, while any
 * authority-bearing caller field is discarded and a changed request rotates
 * to a new operation before it reaches GraphQL.
 */
export const prepareInvoiceFinancialOperationInput = async <
  MutationInput extends Record<string, unknown>,
>({
  action,
  chamberId,
  createOperationId,
  fingerprintRequest,
  input,
  invoiceId,
  storage,
}: Omit<
  RunInvoiceFinancialOperationInput<MutationInput, unknown>,
  "execute" | "isAuthoritativelyCompleted"
>): Promise<PreparedInvoiceFinancialOperationInput<MutationInput>> => {
  const {
    operationId: _untrustedOperationId,
    ...operationFreeInput
  } = input as Record<string, unknown>;
  const authorityField: FinancialOperationAuthorityField = "operationId";
  const storageKey = getInvoiceFinancialOperationStorageKey({
    action,
    chamberId,
    invoiceId,
  });
  const operation = await getOrCreateMemberApplicationOperation({
    ...(createOperationId ? { createOperationId } : {}),
    ...(fingerprintRequest ? { fingerprintRequest } : {}),
    request: {
      action,
      chamberId,
      invoiceId,
      input: operationFreeInput,
    },
    ...(storage ? { storage } : {}),
    storageKey,
  });

  return {
    authorityField,
    input: {
      ...operationFreeInput,
      [authorityField]: operation.operationId,
    } as Record<string, unknown> as MutationInput,
    operationId: operation.operationId,
    storageKey,
  };
};

/**
 * Clears the browser operation only after the backend response proves the
 * financial action completed. Network errors and pending/unknown provider
 * outcomes retain the same key for safe exact replay.
 */
export const runInvoiceFinancialOperation = async <
  MutationInput extends Record<string, unknown>,
  MutationResult,
>({
  execute,
  isAuthoritativelyCompleted,
  ...prepareInput
}: RunInvoiceFinancialOperationInput<
  MutationInput,
  MutationResult
>): Promise<MutationResult> => {
  const prepared = await prepareInvoiceFinancialOperationInput(prepareInput);
  const result = await execute(prepared.input);

  if (isAuthoritativelyCompleted(result)) {
    clearCompletedMemberApplicationOperation({
      operationId: prepared.operationId,
      ...(prepareInput.storage ? { storage: prepareInput.storage } : {}),
      storageKey: prepared.storageKey,
    });
  }

  return result;
};

type RunSpecificInvoiceFinancialOperationInput<MutationInput, MutationResult> =
  Omit<
    RunInvoiceFinancialOperationInput<
      MutationInput & Record<string, unknown>,
      MutationResult
    >,
    "action" | "input"
  > & {
    input: Omit<
      MutationInput,
      FinancialOperationAuthorityField
    >;
  };

export const runCmpInvoiceManualPaymentOperation = <MutationResult>(
  input: RunSpecificInvoiceFinancialOperationInput<
    RecordCmpInvoiceManualPaymentInput,
    MutationResult
  >
) =>
  runInvoiceFinancialOperation<
    RecordCmpInvoiceManualPaymentInput & Record<string, unknown>,
    MutationResult
  >({
    ...input,
    action: "MANUAL_PAYMENT",
  });

export const runCmpMemberManualPaymentOperation = <MutationResult>(
  input: RunSpecificInvoiceFinancialOperationInput<
    RecordCmpMemberManualPaymentInput,
    MutationResult
  >
) =>
  runInvoiceFinancialOperation<
    RecordCmpMemberManualPaymentInput & Record<string, unknown>,
    MutationResult
  >({
    ...input,
    action: "MANUAL_PAYMENT",
  });

export const runCmpInvoiceManualRefundOperation = <MutationResult>(
  input: RunSpecificInvoiceFinancialOperationInput<
    RecordCmpInvoiceManualRefundInput,
    MutationResult
  >
) =>
  runInvoiceFinancialOperation<
    RecordCmpInvoiceManualRefundInput & Record<string, unknown>,
    MutationResult
  >({
    ...input,
    action: "MANUAL_REFUND",
  });
